import { TextareaHTMLAttributes, useCallback, useId, useState } from 'react';
import cn from 'classnames';
import styled from 'styled-components';

import { COLORS } from '@/shared/constants/colors';
import { BFC } from '@/shared/types';
import { Icon } from '@/shared/uikit/Icon';
import { copy } from '@/shared/utils/misc';

export type TextareaProps = TextareaHTMLAttributes<HTMLTextAreaElement> & {
  withCopy?: boolean;
  separator?: string;
  width?: string | number;
  height?: string | number;
  label?: string;
};

export const Textarea: BFC<TextareaProps> = ({
  withCopy = false,
  children,
  className,
  style,
  separator = ',',
  value,
  width,
  height,
  label,
  ...props
}) => {
  const actualValue = Array.isArray(value) ? value.join(separator) : value;
  const [isCoped, setIsCoped] = useState(false);
  const textAreaId = useId();

  const clickCopy = useCallback(async () => {
    if (value === undefined || value === '') return;

    try {
      await copy(String(actualValue));
      setIsCoped(true);

      const timeId = setTimeout(() => {
        setIsCoped(false);
        clearTimeout(timeId);
      }, 2000);
    } catch (e) {
      console.error(e);
    }
  }, [copy, actualValue]);

  const icon = isCoped ? (
    <Icon className="icon" name="check" style={{ color: COLORS.systemMint200 }} size="s20" />
  ) : (
    <Icon onClick={clickCopy} className="icon" name="copy" size="s20" />
  );

  return (
    <Root className={cn(className)}>
      <StyledTextarea
        id={textAreaId}
        value={actualValue}
        style={{ ...style, width, height, paddingTop: label ? 28 : undefined }}
        {...props}
      >
        {children}
      </StyledTextarea>
      <label className="label" htmlFor={textAreaId}>
        {label}
      </label>
      {withCopy && icon}
    </Root>
  );
};

// ===== styles =====

const tokens = {
  padBlock: '8px',
  padInline: '16px',
};

const Root = styled.div`
  position: relative;

  .label {
    position: absolute;
    top: ${tokens.padBlock};
    left: ${tokens.padInline};
    width: calc(100% - 32px);
    height: 24px;
    background: ${COLORS.systemLight200};
    color: ${COLORS.systemDark200};
  }

  .icon {
    position: absolute;
    top: ${tokens.padBlock};
    right: ${tokens.padInline};
  }
`;

const StyledTextarea = styled.textarea`
  height: 100%;
  width: 100%;
  padding: ${tokens.padBlock} ${tokens.padInline};
  border: 0;
  border-radius: 8px;
  font-size: 14px;
  background: ${COLORS.systemLight200};

  &::placeholder {
    color: ${COLORS.systemDark200};
  }
`;
